import { service } from '@ember/service'
import Component from '@glimmer/component'
import { reportingLegend as legend } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'
import { colors } from '@colors'
import convertDateFormat from 'teacher-ui-client/utils/giraffe/convert-date-format'
import type { IntlService } from 'ember-intl'
import { SummaryBox, type Stat } from 'teacher-ui-client/components/giraffe/summary-box'
import type { LegendOptions } from 'highcharts'
import LeftSplitChart from 'teacher-ui-client/components/giraffe/left-split-chart'
import ChartInfoTooltip from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import SeriesColumn, {
  type ChartSpacing,
  type SeriesColumnOptions,
} from 'teacher-ui-client/components/giraffe/primitives/series-column'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'

export interface BooksReadByLevelTotalsData {
  grade_position: number
  summary: {
    total_count: number
    student_count: number
    student_average: number
  }
  months: {
    month: string
    '0-300': number
    '300-500': number
    '500-800': number
    '800-2000': number
  }[]
}

interface Args {
  data: BooksReadByLevelTotalsData
  grade: string
  summaryTitle: string
  title: string
  tooltip: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class BooksReadByLevelTotals extends Component<Signature> {
  @service intl!: IntlService

  readonly colorScheme: SummaryBoxColorScheme = SummaryBoxColorScheme.LightBlue

  readonly chartSpacing: ChartSpacing = [10, 10, 10, 10]

  readonly legend: LegendOptions = legend('left', 'top', 50, -15)

  get categories() {
    const months = this.args.data.months?.map((data) => data.month) ?? []
    return months.map((month) => convertDateFormat(month))
  }

  readonly colorMap = {
    '0-300': colors.oceanyBlue300,
    '300-500': colors.grapeyGreen300,
    '500-800': colors.juicyOrange300,
    '800-2000': colors.watermelonyRed300,
  }

  /**
   * List of column data, extracted from the input data.
   * Formats month data for Highcharts.
   */
  get columns(): SeriesColumnOptions[] {
    const monthData = this.args.data.months ?? []

    return (['0-300', '300-500', '500-800', '800-2000'] as const).map((name) => {
      let label = `${name}L`
      if (name === '800-2000') {
        label = `800L +`
      }
      const color = this.colorMap[name]

      const lexileData = monthData.map((data) => data[name] ?? 0)
      return { name: label, data: lexileData, color, type: 'column' }
    })
  }

  /**
   * List of summary statistics, extracted from the input data.
   * Display total books read and student counts, and average books per student.
   */
  get summaryStats(): Stat[] {
    const summaryData = this.args.data.summary ?? []

    const totalLabel = this.intl.t('reporting.statBoxes.courseProgress.booksRead')
    const countLabel = this.intl.t('reporting.statBoxes.courseProgress.studentCount')
    const perLabel = this.intl.t('reporting.statBoxes.courseProgress.perStudentCount')

    const { total_count, student_count, student_average } = summaryData

    return [
      { label: totalLabel, value: formatNumber(total_count, 1) },
      {
        label: countLabel,
        value: formatNumber(student_count, 1),
      },
      {
        label: perLabel,
        value: formatNumber(student_average, 1),
      },
    ]
  }

  <template>
    <div data-test-giraffe-books-read-by-level-totals class="relative h-full" ...attributes>
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title={{@summaryTitle}}
            @grade={{@grade}}
            @stats={{this.summaryStats}}
            @colorScheme={{this.colorScheme}}
          />
        </:left>
        <:right>
          <ChartInfoTooltip @tooltipContent={{@tooltip}} />
          <SeriesColumn
            @title={{@title}}
            @data={{this.columns}}
            @categories={{this.categories}}
            @stackingStyle="normal"
            @legend={{this.legend}}
            @yAxisReversedStacks={{false}}
            @chartSpacing={{this.chartSpacing}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default BooksReadByLevelTotals
